import {
    IFfsDelegate,
    FfsFlag,
    FfsResponse,
    BaseQuery,
    REQUEST_METHOD,
    INITIAL_OPTION,
} from '@ringcentral/feature-flag-service';
import { FFS_LEVEL_PREFIX, FFS_PREFIX } from '../../constant';
import { jobScheduler } from '../../../job-scheduler';
import type { FFSFetchFn, QueryOptions } from '../../types';

/**
 * implement the interface from ffs sdk
 * implement the network request and flagParser and polling function which are required by ffs sdk
 * the FfsDelegate instance will be passed as a parameter to ffsController(ffs sdk)
 * so those function will be called by ffs sdk
 * @class
 */
class FfsDelegate implements IFfsDelegate {
    private _sendFetch!: FFSFetchFn;
    constructor(sendFetch: FFSFetchFn) {
        this._sendFetch = sendFetch;
    }

    /**
     * the request function to fetch ffs
     * this function will be called by ffs sdk
     * the reason why ffs sdk does not implement the network request logic itself, but only uses RCV's fetch
     * because RCV's fetch handles the problem of token expiration
     * @param baseQuery
     */
    public async doRequest(baseQuery: BaseQuery): Promise<FfsResponse | undefined> {
        const { method, host, path, data, headers } = baseQuery;
        const options: QueryOptions = {
            method,
            url: `${host}${path}`,
            headers: { ...headers, 'Content-Type': 'application/json' } as Record<string, unknown>,
            //platform add credentials:include for all request, but when fetch ffs unauthorized api ,
            //it will cause a CORS error. Actually, we don't need to add credentials:include for ffs api,
            // so here configured it to default value.
            credentials: 'same-origin',
        };
        if (method === REQUEST_METHOD.GET) {
            options.query = data as Record<string, unknown>;
        } else if (method === REQUEST_METHOD.POST) {
            options.body = data as Record<string, unknown>;
        }

        const response = await this._sendFetch(options);

        if (response && response.ok) {
            const result = await response.json();
            /**
             * save the eTag from response header, then add to request header when next fetch
             */
            const eTag = response?.headers.get('ETag');
            if (eTag) {
                return {
                    ...result,
                    eTag,
                };
            }
            return result;
        } else {
            /**
             * ffs backend require the client to use If-None-Match header with ETag value perviously provided for the same request to optimize performance.
             * ffs sdk will add If-None-Match header to request
             * when response status is 304, not handle as exception,return undefined,then we use previous value
             */
            if (response?.status === 304) {
                return undefined;
            }
            throw new Error(`fetch status: ${response?.status},error:${await response?.text()}`);
        }
    }

    /**
     * to handle the parser of flags
     * we can only configure lowercase flags on ffs,
     * and we always use uppercase in our project,
     * so we need to convert the obtained flags to uppercase and remove the prefix at the same time
     * @param rawFlag
     */
    public flagParser(rawFlag: FfsFlag): void {
        Object.keys(rawFlag).forEach(k => {
            if (k.indexOf(`${FFS_LEVEL_PREFIX.USER}`) > -1) {
                rawFlag[k.replace(`${FFS_LEVEL_PREFIX.USER}.`, '')] = rawFlag[k];
                delete rawFlag[k];
            } else if (k.indexOf(`${FFS_LEVEL_PREFIX.APP}`) > -1) {
                rawFlag[k.replace(`${FFS_LEVEL_PREFIX.APP}.`, '')] = rawFlag[k];
                delete rawFlag[k];
            } else if (k.indexOf(`${FFS_PREFIX}`) > -1) {
                rawFlag[k.replace(`${FFS_PREFIX}.`, '')] = rawFlag[k];
                delete rawFlag[k];
            }
        });
    }
    /**
     * start polling job of ffs
     * ffs sdk will call this function and pass the parameters to fetch flags
     * @param key
     * @param executeFunc
     * @param initialOption
     * @param interval
     */
    startScheduleJob(
        key: string,
        executeFunc: () => Promise<void>,
        initialOption: INITIAL_OPTION,
        interval: number
    ): void {
        jobScheduler.schedulePeriodicJob(key, executeFunc, interval, initialOption);
    }

    stopScheduleJob(key: string): void {
        jobScheduler.cancelJob(key);
    }
}
export default FfsDelegate;
